/**
 * redirectMonitorService.js
 * Service for monitoring, tracking, and analyzing redirects
 */

import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Track a redirect event in Firestore
 * 
 * @param {Object} redirectData - Data about the redirect
 * @param {string} redirectData.from - Original URL
 * @param {string} redirectData.to - Destination URL
 * @param {string} redirectData.type - Redirect type (301, 302, etc.)
 * @param {string} redirectData.source - Source of the redirect (direct, search, referral)
 * @param {string} redirectData.userAgent - User agent string
 * @param {string} redirectData.referrer - Referrer URL
 * @returns {Promise<string>} - Document ID of the logged redirect
 */
export async function trackRedirect(redirectData) {
  // Don't track in development
  if (process.env.NODE_ENV === 'development') {
    console.log('[DEV] Redirect tracked:', redirectData);
    return 'dev-mode';
  }

  try {
    // Add timestamp and clean data
    const data = {
      ...redirectData,
      timestamp: serverTimestamp(),
      userAgent: redirectData.userAgent || navigator.userAgent,
      isBot: isBot(redirectData.userAgent || navigator.userAgent)
    };

    // Log to Firestore
    const docRef = await addDoc(collection(db, 'redirects'), data);
    return docRef.id;
  } catch (error) {
    console.error('Error tracking redirect:', error);
    return null;
  }
}

/**
 * Track a broken link (404 error)
 * 
 * @param {Object} errorData - Data about the error
 * @returns {Promise<string>} - Document ID of the logged error
 */
export async function trackBrokenLink(errorData) {
  // Don't track in development
  if (process.env.NODE_ENV === 'development') {
    console.log('[DEV] Broken link tracked:', errorData);
    return 'dev-mode';
  }

  try {
    // Add timestamp and clean data
    const data = {
      ...errorData,
      timestamp: serverTimestamp(),
      userAgent: errorData.userAgent || navigator.userAgent,
      type: '404',
      isBot: isBot(errorData.userAgent || navigator.userAgent)
    };

    // Log to Firestore
    const docRef = await addDoc(collection(db, 'brokenLinks'), data);
    return docRef.id;
  } catch (error) {
    console.error('Error tracking broken link:', error);
    return null;
  }
}

/**
 * Detect common bots and crawlers
 * 
 * @param {string} userAgent - User agent string
 * @returns {boolean} - Whether the user is likely a bot
 */
function isBot(userAgent) {
  const botPatterns = [
    'bot', 'crawler', 'spider', 'crawl', 'slurp', 'GoogleBot', 'BingBot',
    'Yahoo', 'DuckDuckBot', 'Baiduspider', 'YandexBot', 'Sogou',
    'Exabot', 'facebookexternalhit', 'ia_archiver', 'Mediapartners'
  ];

  return botPatterns.some(pattern => 
    userAgent.toLowerCase().includes(pattern.toLowerCase()));
}

/**
 * Get redirect statistics from Firestore (for admin use)
 * 
 * @param {number} days - Number of days to analyze
 * @returns {Promise<Object>} - Redirect statistics
 */
export async function getRedirectStats(days = 30) {
  // This would typically be implemented with Firebase Functions
  // for proper security and to avoid exposing Firestore rules
  // that allow broad read access.
  //
  // For now, we'll just return a mock implementation
  console.log(`Getting redirect stats for last ${days} days`);
  
  // This would be replaced with actual Firestore queries in production
  return {
    total: 123,
    topSources: [
      { source: 'Google', count: 45 },
      { source: 'Direct', count: 32 },
      { source: 'Facebook', count: 18 }
    ],
    mostCommonRedirects: [
      { from: '/property', to: '/contracts', count: 34 },
      { from: '/blog-post-123', to: '/blog/123', count: 26 },
      { from: '/about', to: '/', count: 17 }
    ],
    top404Pages: [
      { path: '/old-page', count: 12 },
      { path: '/broken-link', count: 8 },
      { path: '/missing-image.jpg', count: 5 }
    ]
  };
} 